import { Platform, Share } from 'react-native';
import {
  cacheDirectory,
  deleteAsync,
  StorageAccessFramework,
  writeAsStringAsync,
} from 'expo-file-system/legacy';

const JSON_MIME_TYPE = 'application/json';

export type AccountExportDelivery = 'downloaded' | 'saved' | 'shared' | 'cancelled';

type AccountExportRuntime = {
  platform: string;
  cacheDirectory: string | null;
  requestAndroidDirectory: typeof StorageAccessFramework.requestDirectoryPermissionsAsync;
  createAndroidFile: typeof StorageAccessFramework.createFileAsync;
  write: typeof writeAsStringAsync;
  remove: typeof deleteAsync;
  share: typeof Share.share;
  downloadWeb: (contents: string, filename: string) => void;
};

function downloadWeb(contents: string, filename: string) {
  const browser = globalThis as typeof globalThis & {
    document?: {
      body: { appendChild: (node: unknown) => void; removeChild: (node: unknown) => void };
      createElement: (tag: string) => {
        href: string;
        download: string;
        click: () => void;
      };
    };
    URL?: {
      createObjectURL: (blob: Blob) => string;
      revokeObjectURL: (url: string) => void;
    };
  };
  if (!browser.document || !browser.URL) {
    throw new Error('Browser download is unavailable');
  }
  const BlobConstructor = (globalThis as unknown as {
    Blob: new (parts: string[], options: { type: string }) => Blob;
  }).Blob;
  const objectUrl = browser.URL.createObjectURL(
    new BlobConstructor([contents], { type: `${JSON_MIME_TYPE};charset=utf-8` }),
  );
  const anchor = browser.document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  browser.document.body.appendChild(anchor);
  anchor.click();
  browser.document.body.removeChild(anchor);
  browser.URL.revokeObjectURL(objectUrl);
}

const defaultRuntime: AccountExportRuntime = {
  platform: Platform.OS,
  cacheDirectory,
  requestAndroidDirectory: (...args) => (
    StorageAccessFramework.requestDirectoryPermissionsAsync(...args)
  ),
  createAndroidFile: (...args) => StorageAccessFramework.createFileAsync(...args),
  write: writeAsStringAsync,
  remove: deleteAsync,
  share: (content, options) => Share.share(content, options),
  downloadWeb,
};

export function accountExportFilename(now = new Date()): string {
  return `nosubject-account-data-${now.toISOString().slice(0, 10)}.json`;
}

export function serializeAccountExport(data: Record<string, unknown>): string {
  return `${JSON.stringify(data, null, 2)}\n`;
}

const ACCOUNT_EXPORT_ARRAYS = [
  ['messages', 'messages'],
  ['transactions', 'transactions'],
  ['contact_prices', 'contact_prices'],
  ['contact_preferences', 'contact_preferences'],
  ['education', 'education'],
  ['professions', 'professions'],
  ['calendar_connections', 'calendar_connections'],
  ['google_calendar_cache', 'google_calendar_cache'],
  ['reservations', 'reservations'],
  ['reservation_reschedule_requests', 'reservation_reschedule_requests'],
  ['reservation_calls', 'reservation_calls'],
] as const;

/** Refuse to write a plausible-looking partial or unknown-version package. */
export function assertCompleteAccountExport(data: Record<string, unknown>): void {
  if (data.export_schema_version !== 1 || data.export_status !== 'complete') {
    throw new Error('Unsupported or incomplete account export');
  }
  if (typeof data.exported_at !== 'string' || !Number.isFinite(Date.parse(data.exported_at))) {
    throw new Error('Invalid account export timestamp');
  }
  const counts = data.record_counts;
  if (!counts || typeof counts !== 'object' || Array.isArray(counts)) {
    throw new Error('Missing account export record counts');
  }
  const countRecord = counts as Record<string, unknown>;
  for (const [countKey, dataKey] of ACCOUNT_EXPORT_ARRAYS) {
    const expected = countRecord[countKey];
    const records = data[dataKey];
    if (!Number.isSafeInteger(expected) || (expected as number) < 0 || !Array.isArray(records)) {
      throw new Error(`Invalid account export collection: ${countKey}`);
    }
    if (records.length !== expected) {
      throw new Error(`Incomplete account export collection: ${countKey}`);
    }
  }
  const instagramCount = countRecord.instagram_connections;
  if (!Number.isSafeInteger(instagramCount)
      || ((data.instagram_connection == null ? 0 : 1) !== instagramCount)) {
    throw new Error('Incomplete account export collection: instagram_connections');
  }
}

export async function deliverAccountExport(
  data: Record<string, unknown>,
  now = new Date(),
  runtime: AccountExportRuntime = defaultRuntime,
): Promise<AccountExportDelivery> {
  assertCompleteAccountExport(data);
  const filename = accountExportFilename(now);

  if (runtime.platform === 'web') {
    const contents = serializeAccountExport(data);
    runtime.downloadWeb(contents, filename);
    return 'downloaded';
  }

  if (runtime.platform === 'android') {
    const permission = await runtime.requestAndroidDirectory();
    if (!permission.granted) return 'cancelled';
    const contents = serializeAccountExport(data);
    const uri = await runtime.createAndroidFile(
      permission.directoryUri,
      filename.replace(/\.json$/u, ''),
      JSON_MIME_TYPE,
    );
    await runtime.write(uri, contents);
    return 'saved';
  }

  if (!runtime.cacheDirectory) {
    throw new Error('Account export cache is unavailable');
  }
  const contents = serializeAccountExport(data);
  const uri = `${runtime.cacheDirectory}${filename}`;
  await runtime.write(uri, contents);
  try {
    const result = await runtime.share({ title: filename, url: uri }, { subject: filename });
    return result.action === Share.dismissedAction ? 'cancelled' : 'shared';
  } finally {
    await runtime.remove(uri, { idempotent: true }).catch(() => undefined);
  }
}
